import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import ArrowOut from '../icon/arrow-out'

const buttonVariants = cva('', {
  variants: {
    colour: {
      'ink-blue':
        'text-blue-d! border-blue-d dark:text-white! dark:border-white',
      'mid-blue':
        'text-blue-m! border-blue-m dark:text-white! dark:border-white',
      chartreuse: 'text-char! border-char antialiased',
      white: 'text-white! border-white antialiased'
    },
    fill: {
      'ink-blue': 'fill-blue-d dark:fill-white',
      'mid-blue': 'fill-blue-m dark:fill-white',
      chartreuse: 'fill-char',
      white: 'fill-white'
    },
    stroke: {
      'ink-blue': 'stroke-blue-d dark:stroke-white',
      'mid-blue': 'stroke-blue-m dark:stroke-white',
      chartreuse: 'stroke-char',
      white: 'stroke-white'
    },
    hover: {
      'ink-blue': 'group-hover:bg-blue-d-20 group-focus:bg-blue-d-20',
      'mid-blue': 'group-hover:bg-blue-m-20 group-focus:bg-blue-m-20',
      chartreuse: 'group-hover:bg-char/20 group-focus:bg-char/20',
      white: 'group-hover:bg-white/20 group-focus:bg-white/20'
    }
  },
  defaultVariants: {
    colour: 'ink-blue',
    fill: 'ink-blue',
    stroke: 'ink-blue',
    hover: 'ink-blue'
  }
})

const SecondaryCTA = ({ data, colour, size }: Button) => {
  const btnColour = data?.strongColour
    ? colour === 'ink-blue'
      ? 'mid-blue'
      : 'chartreuse'
    : colour

  return (
    <>
      {!data?.disabled ? (
        <a
          href={
            data?.internalLink?.sys?.uri
              ? data?.internalLink?.sys.uri
              : (data?.externalLink ?? '')
          }
          {...(data?.ariaLabel !== '' ? { 'aria-label': data?.ariaLabel } : {})}
          className='button unstyled-link group relative flex h-min'
          {...(data?.externalLink && data?.newWindow
            ? { target: '_blank' }
            : {})}
          {...(data?.externalLink && data?.newWindow
            ? { rel: 'noreferrer nofollow' }
            : {})}
        >
          {size !== 'Small' && (
            <svg height='100%' width='100%' className='absolute top-0 left-0'>
              <rect
                x={0}
                y={0}
                width={10}
                height={10}
                className={cn(
                  buttonVariants({
                    fill: btnColour,
                    stroke: null,
                    colour: null,
                    hover: null
                  }),
                  'origin-left translate-y-[calc(50%-5px)] scale-x-0 transition-transform duration-200 group-hover:scale-x-100 group-focus:scale-x-100 sm:h-[12px] sm:w-[12px] sm:translate-y-[calc(50%-6px)]'
                )}
                strokeWidth='0'
                strokeLinecap='square'
              />
              <rect
                x='0'
                y='0'
                height='100%'
                width={2}
                strokeWidth='2'
                strokeLinecap='square'
                className={cn(
                  buttonVariants({
                    fill: btnColour,
                    stroke: null,
                    colour: null,
                    hover: null
                  }),
                  'h-[calc(50%-5px)] origin-left transition-transform duration-200 group-hover:translate-x-[10px] group-focus:translate-x-[10px] sm:ml-[2px] sm:h-[calc(50%-6px)] sm:group-hover:translate-x-[12px] sm:group-focus:translate-x-[12px]'
                )}
              />
              <rect
                x='12'
                y='0'
                height={2}
                width={10}
                strokeWidth='2'
                strokeLinecap='square'
                className={cn(
                  buttonVariants({
                    fill: btnColour,
                    stroke: null,
                    colour: null,
                    hover: null
                  }),
                  'origin-left translate-y-[calc(50%-7px)] scale-x-0 transition-transform duration-200 group-hover:scale-x-100 group-focus:scale-x-100 sm:w-[14px] sm:translate-y-[calc(50%-8px)]'
                )}
              />
              <rect
                x='0'
                y='0'
                height={14}
                width={2}
                strokeWidth='2'
                strokeLinecap='square'
                className={cn(
                  buttonVariants({
                    fill: btnColour,
                    stroke: null,
                    colour: null,
                    hover: null
                  }),
                  'origin-left translate-y-[calc(50%-7px)] transition-transform duration-200 group-hover:translate-x-[20px] group-focus:translate-x-[20px] sm:h-[16px] sm:translate-y-[calc(50%-8px)] sm:group-hover:translate-x-[24px] sm:group-focus:translate-x-[24px]'
                )}
              />
              <rect
                x='12'
                y='0'
                height={2}
                width={10}
                strokeWidth='2'
                strokeLinecap='square'
                className={cn(
                  buttonVariants({
                    fill: btnColour,
                    stroke: null,
                    colour: null,
                    hover: null
                  }),
                  'origin-left translate-y-[calc(50%+5px)] scale-x-0 transition-transform duration-200 group-hover:scale-x-100 group-focus:scale-x-100 sm:w-[14px] sm:translate-y-[calc(50%+6px)]'
                )}
              />
              <rect
                x='0'
                y='0'
                height='100%'
                width={2}
                strokeWidth='2'
                strokeLinecap='square'
                className={cn(
                  buttonVariants({
                    fill: btnColour,
                    stroke: null,
                    colour: null,
                    hover: null
                  }),
                  'absolute h-[calc(50%-5px)] origin-left translate-y-[calc(50%+5px)] transition-transform duration-200 group-hover:translate-x-[10px] group-focus:translate-x-[10px] sm:h-[calc(50%-6px)] sm:translate-y-[calc(50%+6px)] sm:group-hover:translate-x-[12px] sm:group-focus:translate-x-[12px]'
                )}
              />
            </svg>
          )}

          <div
            className={cn(
              buttonVariants({
                fill: null,
                stroke: null,
                colour: btnColour,
                hover: size === 'Small' ? btnColour : null
              }),
              'sm:gap-xs gap-2xs-plus flex items-center border-2 tracking-normal shadow-none transition-all duration-200',
              `${size === 'Small' ? 'px-xs py-[6px] text-[14px]' : 'px-xs-plus mr-[10px] border-l-0 py-[14px] group-hover:mr-0 group-hover:ml-[10px] group-hover:border-l-0 group-focus:mr-0 group-focus:ml-[10px] group-focus:border-l-0 sm:mr-[12px] sm:ml-0 sm:px-[24px] sm:py-[18px] sm:group-hover:ml-[12px] sm:group-focus:ml-[12px]'}`
            )}
          >
            {data?.mobileLabel ? (
              <>
                <span className='lg:hidden'>{data?.mobileLabel}</span>
                <span className='max-lg:hidden'>{data?.label}</span>
              </>
            ) : (
              <>{data?.label}</>
            )}
            {!data?.internalLink && data?.newWindow && (
              <ArrowOut
                fill={''}
                width={`${size === 'Small' ? '0.75rem' : '1.125rem'}`}
                height={`${size === 'Small' ? '0.75rem' : '1.125rem'}`}
                title={''}
                className='-mt-[3px] max-sm:-mt-[2px] max-sm:h-[14px] max-sm:w-[14px]'
              />
            )}
          </div>
        </a>
      ) : (
        <div
          className={`button gap-xs border-grey-m text-grey-m flex cursor-not-allowed items-center border-2 ${size === 'Small' ? 'px-xs py-[6px] text-[14px]' : 'mr-xs-plus py-xs sm:mr-s px-[20px] sm:px-[24px] sm:py-[20px]'}`}
          aria-disabled
        >
          {data?.label}
          {!data?.internalLink && data?.newWindow && (
            <ArrowOut
              fill={''}
              width={`${size === 'Small' ? '0.75rem' : '1.125rem'}`}
              height={`${size === 'Small' ? '0.75rem' : '1.125rem'}`}
              title={''}
            />
          )}
        </div>
      )}
    </>
  )
}

export default SecondaryCTA
